import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Star, Eye, CheckCircle } from "@/components/ui/icons";

export type SignalType = "helpful" | "worth_reading" | "best_answer";

export interface CommunitySignalRow {
  id: string;
  user_id: string;
  signal_type: string;
}

// Static map (index lookup, not a function-call result) — safe under the
// react-hooks/static-components rule.
const SIGNAL_META = {
  best_answer: { label: "Best answer", Icon: CheckCircle },
  helpful: { label: "Helpful", Icon: Star },
  worth_reading: { label: "Worth reading", Icon: Eye },
} as const;
const SIGNAL_ORDER: SignalType[] = ["best_answer", "helpful", "worth_reading"];

interface MessageCommunitySignalsProps {
  signals: readonly CommunitySignalRow[] | undefined;
  currentUserId: string | undefined;
  onSignal: (type: SignalType) => void;
}

/**
 * Per-message community-curation chips (helpful / worth_reading / best_answer).
 * Mirrors `MessageReactions`: one chip per signal type that has ≥1 mark, with
 * a count and an own-mark highlight. Generic marks are toggleable; best-answer
 * marks are read-only because they are owned by the thread Q&A acceptance
 * controls. Data comes from the channel-scoped
 * `useChannelCommunitySignalsMap` (stable per message), so this renders
 * nothing until a message is actually signaled.
 */
export function MessageCommunitySignals({
  signals,
  currentUserId,
  onSignal,
}: MessageCommunitySignalsProps) {
  if (!signals || signals.length === 0) return null;

  const byType = new Map<SignalType, { count: number; hasOwn: boolean }>();
  for (const s of signals) {
    const t = s.signal_type as SignalType;
    if (!SIGNAL_META[t]) continue;
    const cur = byType.get(t) ?? { count: 0, hasOwn: false };
    cur.count += 1;
    if (currentUserId && s.user_id === currentUserId) cur.hasOwn = true;
    byType.set(t, cur);
  }
  if (byType.size === 0) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="mt-1 flex flex-wrap gap-1">
        {SIGNAL_ORDER.filter((t) => byType.has(t)).map((t) => {
          const entry = byType.get(t);
          if (!entry) return null;
          const { label, Icon } = SIGNAL_META[t];
          const canToggle = t !== "best_answer";
          return (
            <Tooltip key={t}>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="tiny"
                  disabled={!canToggle}
                  onClick={() => {
                    if (canToggle) onSignal(t);
                  }}
                  className={cn(
                    "h-auto gap-1 rounded-full border px-1.5 py-0.5 text-xs font-normal",
                    entry.hasOwn
                      ? "bg-info-muted border-info/40"
                      : "border-border bg-foreground/[0.06] hover:bg-foreground/[0.08]",
                  )}
                >
                  <Icon size={12} weight="Bold" className="shrink-0" />
                  <span>{label}</span>
                  <span className="text-muted-foreground">{entry.count}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top">
                {canToggle
                  ? entry.hasOwn
                    ? `Click to remove your "${label}" mark`
                    : `Mark this as "${label}"`
                  : "Accepted through the thread answer controls"}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </div>
    </TooltipProvider>
  );
}
